import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from 'react';

// Set the WebSocket server URL - default to localhost in development
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8082';

// Define types
type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

interface ServerInfo {
  uptime: number;
  connections: number;
  timestamp: string;
}

// Define the WebSocket context type
interface WebSocketContextType {
  socket: WebSocket | null;
  status: ConnectionStatus;
  statusMessage: string;
  connectionId: string;
  roomId: string;
  isNewRoom: boolean;
  serverInfo: ServerInfo | null;
  names: string[];
  latency: number;
  
  // Methods
  addName: (name: string) => void;
  removeName: (name: string) => void;
  getNames: () => void;
  sendPing: () => void;
}

// Create the context with default values
const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  status: 'disconnected',
  statusMessage: 'Not connected',
  connectionId: '',
  roomId: '',
  isNewRoom: false,
  serverInfo: null,
  names: [],
  latency: 0,
  
  // Default empty methods
  addName: () => {},
  removeName: () => {},
  getNames: () => {},
  sendPing: () => {},
});

// Each tab should have its own connection and room
// (removed singleton pattern)

interface WebSocketProviderProps {
  children: ReactNode;
  url?: string;
}

// Provider component
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ 
  children, 
  url = WS_URL
}) => {
  // State
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [statusMessage, setStatusMessage] = useState<string>('Not connected');
  const [connectionId, setConnectionId] = useState<string>('');
  const [roomId, setRoomId] = useState<string>('');
  const [isNewRoom, setIsNewRoom] = useState<boolean>(false);
  const [serverInfo, setServerInfo] = useState<ServerInfo | null>(null);
  const [names, setNames] = useState<string[]>([]);
  const [latency, setLatency] = useState<number>(0);
  
  // Refs to track component lifecycle and prevent memory leaks
  const isMountedRef = useRef(true);
  const pingIntervalRef = useRef<number | null>(null);
  const connectionAttemptRef = useRef<number>(0);
  const lastConnectionAttemptRef = useRef<number>(0);
  
  // Connect to WebSocket
  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;

    // Handle incoming messages (moved inside useEffect to prevent dependency issues)
    const handleMessage = (message: any) => {
      //console.log('[WebSocketContext] Received message:', message);
      
      // Only process messages if component is still mounted
      if (!isMountedRef.current) return;
      
      switch (message.type) {
        case 'connection-established':
          console.log('[WebSocketContext] Connection established with ID:', message.connectionId, 'room:', message.roomId);
          setConnectionId(message.connectionId || '');
          setRoomId(message.roomId || '');
          setIsNewRoom(message.isNewRoom || false);
          if (message.serverInfo) {
            setServerInfo(message.serverInfo);
          }
          break;
          
        case 'room-joined':
          console.log('[WebSocketContext] Joined room:', message.roomId);
          setRoomId(message.roomId || '');
          setIsNewRoom(message.isNewRoom || false);
          if (message.serverInfo) {
            setServerInfo(message.serverInfo);
          }
          break;
          
        case 'room-names':
          console.log('[WebSocketContext] Received names for room:', message.names);
          if (Array.isArray(message.names)) {
            setNames(message.names);
          } else {
            console.error('[WebSocketContext] Expected names array but got:', message.names);
            setNames([]);
          }
          break;
          
        // Backward compatibility for 'room-champions'
        case 'room-champions':
          console.log('[WebSocketContext] Received champions for room (converting to names):', message.champions);
          if (Array.isArray(message.champions)) {
            setNames(message.champions);
          }
          break;
          
        case 'pong':
          if (message.clientTimestamp) {
            const roundTripTime = Date.now() - message.clientTimestamp;
            setLatency(roundTripTime);
          }
          break;
          
        case 'error':
          console.error('[WebSocketContext] Server error:', message.message);
          break;
          
        default:
        //  console.log('[WebSocketContext] Unhandled message type:', message.type);
      }
    };
    
    // Function to create a new WebSocket connection
    const createConnection = () => {
      // Prevent creating connection if one already exists and is open
      if (socket && (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)) {
        console.log('[WebSocketContext] Connection already exists, skipping new connection attempt');
        return;
      }
      
      // Prevent rapid reconnection attempts
      const now = Date.now();
      const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
      
      if (timeSinceLastAttempt < 1000) { // Minimum 1 second between attempts
        console.log('[WebSocketContext] Throttling connection attempt - too soon since last attempt');
        return;
      }
      
      connectionAttemptRef.current += 1;
      lastConnectionAttemptRef.current = now;
      
      console.log('[WebSocketContext] Establishing new connection to', url, '(attempt', connectionAttemptRef.current, ')');
      setStatus('connecting');
      setStatusMessage('Connecting to server...');
      
      // Create a new WebSocket connection (each tab gets its own)
      try {
        // Validate URL format before creating WebSocket
        try {
          new URL(url);
        } catch (urlError) {
          console.error('[WebSocketContext] Invalid WebSocket URL:', url, urlError);
          throw new Error(`Invalid WebSocket URL: ${url}`);
        }
        
        const ws = new WebSocket(url);
        setSocket(ws);
        
        // Connection opened
        ws.onopen = () => {
          if (!isMountedRef.current) return;
          
          console.log('[WebSocketContext] Connection established successfully');
          setStatus('connected');
          setStatusMessage('Connected to server');
          
          // Setup ping interval now that connection is established
          if (pingIntervalRef.current !== null) {
            clearInterval(pingIntervalRef.current);
          }
          pingIntervalRef.current = window.setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ 
                type: 'ping',
                timestamp: Date.now()
              }));
            }
          }, 30000) as unknown as number;
          
          // Request connection info as soon as we connect
          try {
            ws.send(JSON.stringify({ type: 'get-connection-info' }));
          } catch (err) {
            console.error('[WebSocketContext] Error requesting connection info:', err);
          }
        };
        
        // Listen for messages
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleMessage(data);
          } catch (error) {
            console.error('[WebSocketContext] Error parsing message:', error);
          }
        };
        
        // Connection closed
        ws.onclose = (event) => {
          if (!isMountedRef.current) return;
          
          console.log(`[WebSocketContext] Connection closed: Code ${event.code}`);
          
          // Clear ping interval when connection closes
          if (pingIntervalRef.current !== null) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          
          setSocket(null);
          setStatus('disconnected');
          setStatusMessage(`Connection closed: Code ${event.code}`);
          
          // Try to reconnect in 3 seconds if unexpected close
          if (event.code !== 1000 && event.code !== 1001 && isMountedRef.current) {
            console.log('[WebSocketContext] Will attempt to reconnect in 3 seconds...');
            setTimeout(() => {
              if (isMountedRef.current) {
                createConnection();
              }
            }, 3000);
          }
        };
        
        // Error handling
        ws.onerror = (error) => {
          if (!isMountedRef.current) return;
          
          console.error('[WebSocketContext] WebSocket error occurred:', error);
          setStatus('disconnected');
          setStatusMessage('Connection error');
        };
      } catch (err) {
        console.error('[WebSocketContext] Failed to create WebSocket:', err);
        setStatus('disconnected');
        setStatusMessage('Failed to connect');
      }
    };
    
    // Create initial connection
    createConnection();
    
    // Setup ping interval - will be created after connection is established
    
    // Cleanup when unmounting
    return () => {
      console.log('[WebSocketContext] Component unmounting, cleaning up');
      isMountedRef.current = false;
      
      // Clear ping interval
      if (pingIntervalRef.current !== null) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      // Close the socket connection when unmounting (each tab has its own)
      if (socket && socket.readyState === WebSocket.OPEN) {
        console.log('[WebSocketContext] Closing WebSocket connection on unmount');
        socket.close();
      }
    };
  }, [url, socket]); // Remove socket dependency to prevent reconnection loops

  // Add a name to the room
  const addName = useCallback((name: string) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.log('[WebSocketContext] Adding name:', name, 'to room:', roomId);
      
      // Try the new 'add-name' message type first
      socket.send(JSON.stringify({ 
        type: 'add-name',
        name
      }));
      
      // For backward compatibility, also send as 'add-champion'
      socket.send(JSON.stringify({
        type: 'add-champion',
        champion: name
      }));
      
      // Request updated list after adding
      setTimeout(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'get-names' }));
          socket.send(JSON.stringify({ type: 'get-champions' }));
        }
      }, 300);
    } else {
      console.error('[WebSocketContext] Cannot add name - not connected');
    }
  }, [socket, roomId]);

  // Remove a name from the room
  const removeName = useCallback((name: string) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.log('[WebSocketContext] Removing name:', name, 'from room:', roomId);
      
      // Try the new 'remove-name' message type first
      socket.send(JSON.stringify({ 
        type: 'remove-name',
        name
      }));
      
      // For backward compatibility, also send as 'remove-champion'
      socket.send(JSON.stringify({
        type: 'remove-champion',
        champion: name
      }));
      
      // Request updated list after removing
      setTimeout(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'get-names' }));
          socket.send(JSON.stringify({ type: 'get-champions' }));
        }
      }, 300);
    } else {
      console.error('[WebSocketContext] Cannot remove name - not connected');
    }
  }, [socket, roomId]);

  // Get names in the room
  const getNames = useCallback(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.log('[WebSocketContext] Requesting names for room:', roomId);
      
      // Try the new 'get-names' message type first
      socket.send(JSON.stringify({ type: 'get-names' }));
      
      // For backward compatibility, also send as 'get-champions'
      socket.send(JSON.stringify({ type: 'get-champions' }));
    } else {
      console.error('[WebSocketContext] Cannot get names - not connected');
    }
  }, [socket, roomId]);

  // Send a ping to measure latency
  const sendPing = useCallback(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ 
        type: 'ping',
        timestamp: Date.now()
      }));
    }
  }, [socket]);

  // Context value
  const value = {
    socket,
    status,
    statusMessage,
    connectionId,
    roomId,
    isNewRoom,
    serverInfo,
    names,
    latency,
    
    addName,
    removeName,
    getNames,
    sendPing
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook for using the WebSocket context
export const useWebSocket = () => useContext(WebSocketContext);

export default WebSocketContext; 